import './NavTabs.css'

import { clsx } from 'clsx'
import type { MouseEvent, ReactElement, ReactNode, Ref } from 'react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { NavLinkProps, To } from 'react-router'
import { NavLink, useLocation } from 'react-router'

import { Badge } from '#design-system/component/Badge/Badge'
import { FlyOut } from '#design-system/component/FlyOut/FlyOut'
import { Tooltip } from '#design-system/component/Tooltip/Tooltip'
import { useSlidingIndicator } from '#design-system/hooks/useSlidingIndicator'
import { useTabOverflow } from '#design-system/hooks/useTabOverflow'

export interface NavTabItem {
  to: NavLinkProps['to']
  label: string
  icon?: ReactElement | undefined
  /** Trailing count indicator (a count Badge). Undefined or 0 ⇒ nothing rendered. */
  count?: number | undefined
  /** When set, the tab is disabled and the message is surfaced via a tooltip. Mirrors Button. */
  disabledMessage?: string | undefined
}

export interface NavTabsProps {
  items: NavTabItem[]
  orientation?: 'horizontal' | 'vertical'
  /** Optional click intercept: preventDefault + call with the item's `to`. Lets a consumer navigate
   *  somewhere other than `to` (e.g. resume a section's last-visited route). Omit for plain native navigation. */
  onNavigate?: ((to: To) => void) | undefined
  /** Names the `<nav>` landmark — required so assistive tech can distinguish multiple navs. */
  'aria-label': string
  className?: string | undefined
  ref?: Ref<HTMLElement> | undefined
}

/** Stable identity for an item whose `to` may be a string or a partial Path. Search and hash are
 *  part of it: two tabs on the same pathname are different destinations. */
const keyForTo = (to: To): string =>
  typeof to === 'string' ? to : `${to.pathname ?? ''}${to.search ?? ''}${to.hash ?? ''}`

/**
 * Filled navigation tabs: a `<nav>` of `NavLink`s with a sliding neutral pill behind the
 * route-active tab. Active state is route-driven (NavLink sets `aria-current="page"`) with
 * section-prefix matching, so a tab stays active anywhere within its section. A tab may also show a count.
 *
 * Horizontal by default; `orientation="vertical"` stacks full-width tabs (the app shell's primary
 * nav). A horizontal row too narrow for every tab collapses the surplus into a fly-out behind a
 * trailing `n more` tab, keeping the route-active tab in the row and never reading `1 more`.
 * Vertical has no inline overflow to resolve, so it opts out.
 */
export const NavTabs = (props: NavTabsProps): ReactElement => {
  const { items, orientation = 'horizontal', onNavigate, className, ref } = props
  const ariaLabel = props['aria-label']
  const { t } = useTranslation('design_system')
  const { pathname } = useLocation()

  const navRef = useRef<HTMLElement | null>(null)
  const [isMoreOpen, setIsMoreOpen] = useState(false)

  const isHorizontal = orientation === 'horizontal'

  // Declared before the indicator below, so its measurement lands first.
  const overflowing = useTabOverflow(navRef, {
    activeKey: pathname,
    ids: items.map((item) => keyForTo(item.to)),
  })
  const overflowItems = items.filter((item) => overflowing.has(keyForTo(item.to)))
  const hasOverflow = overflowItems.length > 0

  // Slide the pill onto the route-active tab. Keyed on what is stowed too: navigating to a stowed
  // tab pulls it into the row a commit later, without the path changing again.
  useSlidingIndicator(navRef, `${pathname}|${[...overflowing].join(' ')}`)

  const setNavRef = (node: HTMLElement | null): void => {
    navRef.current = node
    if (typeof ref === 'function') {
      ref(node)
    } else if (ref) {
      ref.current = node
    }
  }

  const handleNavigate =
    (to: To) =>
    (event: MouseEvent<HTMLAnchorElement>): void => {
      if (!onNavigate) {
        return
      }
      event.preventDefault()
      onNavigate(to)
    }

  const renderCount = (count: number | undefined): ReactNode => {
    if (count === undefined || count <= 0) {
      return null
    }
    return <Badge aria-label={t('nav_tabs.notification_count', { count })} label={count} />
  }

  const renderBody = (item: NavTabItem): ReactNode => (
    <>
      {item.icon ?? null}
      <span className="label" data-label={item.label}>
        {item.label}
      </span>
      {renderCount(item.count)}
    </>
  )

  const renderTab = (item: NavTabItem): ReactElement => {
    const { disabledMessage } = item
    const key = keyForTo(item.to)
    const stowed = overflowing.has(key)
    if (disabledMessage !== undefined) {
      return (
        <Tooltip key={key} title={disabledMessage}>
          <span
            aria-disabled="true"
            className={clsx('tab', 'disabled', stowed && 'stowed')}
            inert={stowed}
          >
            {renderBody(item)}
          </span>
        </Tooltip>
      )
    }
    return (
      <NavLink
        key={key}
        className={({ isActive }) => clsx('tab', isActive && 'active', stowed && 'stowed')}
        inert={stowed}
        onClick={onNavigate ? handleNavigate(item.to) : undefined}
        to={item.to}
      >
        {renderBody(item)}
      </NavLink>
    )
  }

  const renderMenuItem = (item: NavTabItem): ReactElement => {
    const { disabledMessage } = item
    if (disabledMessage !== undefined) {
      return (
        <Tooltip key={keyForTo(item.to)} title={disabledMessage}>
          <span aria-disabled="true" className="more-item" role="menuitem">
            {renderBody(item)}
          </span>
        </Tooltip>
      )
    }
    return (
      <NavLink
        key={keyForTo(item.to)}
        className={({ isActive }) => clsx('more-item', isActive && 'selected')}
        onClick={(event) => {
          setIsMoreOpen(false)
          if (onNavigate) {
            event.preventDefault()
            onNavigate(item.to)
          }
        }}
        role="menuitem"
        to={item.to}
      >
        {renderBody(item)}
      </NavLink>
    )
  }

  // Always mounted, just stowed when unneeded, so its width is always measurable.
  // The hidden bold `data-label` copy reserves the widest `n` the row could show to prevent
  // jitter/oscillation as the count changes during resizes.
  const moreTab = (
    <button
      className={clsx('tab', 'more', !hasOverflow && 'stowed')}
      inert={!hasOverflow}
      type="button"
    >
      <span className="label" data-label={t('tabs.more', { count: items.length })}>
        {t('tabs.more', { count: overflowItems.length })}
      </span>
    </button>
  )

  return (
    <nav
      ref={setNavRef}
      aria-label={ariaLabel}
      className={clsx(
        'doss-nav-tabs',
        'doss-slidable',
        orientation,
        isHorizontal && 'doss-overflowable',
        className,
      )}
    >
      {items.map(renderTab)}
      {isHorizontal ? (
        <FlyOut
          aria-label={t('tabs.more_tabs')}
          className="doss-overflow-menu"
          isOpen={isMoreOpen}
          onOpenChange={setIsMoreOpen}
          role="menu"
          trigger={{ element: moreTab }}
        >
          {overflowItems.map(renderMenuItem)}
        </FlyOut>
      ) : null}
    </nav>
  )
}
